import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Users, Search, Mic } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";
import { IMG } from "../lib/site";

const pillars = [
  { icon: Compass, title: "Partnership Strategy & System Design", body: "Helping organizations, state leaders, and communities design learner-centered ecosystems that authentically connect education, workforce, and economic opportunity." },
  { icon: Users, title: "Facilitation & Convening", body: "Designing meetings people actually remember — collaborations where relationships deepen, ideas emerge, and decisions move forward." },
  { icon: Search, title: "Research & Insight", body: "Turning labor market information, policy, stakeholder voices, and global examples into practical recommendations." },
  { icon: Mic, title: "Advising & Thought Partnership", body: "Conference sessions, topic-based workshops, and strategic advising focused on the future of learning and work." },
];

// Checkerboard color scheme — visually distinct pairs
const pillarStyles = [
  { bg: "bg-cobalt", text: "text-cream", accent: "bg-orange-brand" },       // 0 blue
  { bg: "bg-orange-brand", text: "text-cream", accent: "bg-cobalt" },        // 1 orange
  { bg: "bg-orange-brand", text: "text-cream", accent: "bg-cobalt" },        // 2 orange
  { bg: "bg-cobalt", text: "text-cream", accent: "bg-orange-brand" },        // 3 blue
];

const principles = [
  { title: "Start with people.", body: "Policy matters. Data matters. But sustainable change begins with the experiences of learners, educators, employers, and communities." },
  { title: "Stay curious.", body: "Questions often create more progress than quick answers. We explore broadly before narrowing intentionally." },
  { title: "Design with optimism.", body: "Change isn't a problem to solve. It's an opportunity to build something better." },
  { title: "Make ideas actionable.", body: "Vision without implementation stays on a slide deck. Every engagement ends with practical next steps and shared ownership." },
  { title: "Leave room for joy.", body: "Laughter creates trust. Creativity unlocks possibility. The best work happens when people feel comfortable enough to think differently." },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* ---------- WHAT WE DO ---------- */}
      <section className="pt-12 lg:pt-16 pb-14 lg:pb-16 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-12">
            <Reveal>
              <div className="overline">What we do</div>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink">
                Curiosity, translated<br />into action.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                No two organizations need exactly the same support. Every engagement is designed around your goals,
                timeline, and stakeholders — but every engagement starts with the same conviction: better systems
                are built together.
              </p>
            </Reveal>
          </div>

          {/* Colorful bento of 4 pillars — alternating cobalt & orange, each clickable to matching Services section */}
          <div className="grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="pillars-grid">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const s = pillarStyles[i];
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <Link
                    to={`/services#svc-${i}`}
                    data-testid={`pillar-${i}`}
                    className={`${s.bg} ${s.text} relative p-10 lg:p-14 h-full flex flex-col justify-between min-h-[320px] overflow-hidden group hover:-translate-y-1 transition-transform duration-500 focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-cream cursor-pointer`}
                  >
                    {/* Corner accent shape */}
                    <div
                      className={`absolute -top-8 -right-8 w-24 h-24 ${s.accent} opacity-90 rounded-full transition-transform duration-500 group-hover:scale-125`}
                      aria-hidden="true"
                    />
                    <div className="relative">
                      <div className="w-14 h-14 flex items-center justify-center border-2 border-cream/50 rounded-sm mb-8 backdrop-blur-sm">
                        <Icon size={22} className="text-cream" strokeWidth={1.75} />
                      </div>
                      <h3 className="font-display text-2xl lg:text-3xl leading-tight max-w-sm">
                        {p.title}
                      </h3>
                    </div>
                    <div className="relative mt-6">
                      <p className="text-cream/95 leading-relaxed">{p.body}</p>
                      <span className="inline-flex items-center gap-2 mt-6 text-xs tracking-[0.2em] uppercase font-medium border-b border-cream/60 pb-1 group-hover:border-cream group-hover:gap-3 transition-all">
                        Learn more <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="pt-16 lg:pt-24 pb-24 lg:pb-32 relative overflow-hidden">
        <CobaltTriangle className="absolute top-0 right-0 opacity-10" size={280} flip />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="overline">Our approach</div>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal delay={0.05}>
                <p className="font-display text-3xl sm:text-4xl lg:text-[2.5rem] leading-[1.2] text-ink">
                  The best collaborations are ambitious, practical, and enjoyable.
                  <span className="block mt-4 text-ink-soft text-xl lg:text-2xl font-sans not-italic leading-relaxed">
                    Our work is grounded in a few principles that shape every project we take on.
                  </span>
                </p>
              </Reveal>
            </div>
          </div>

          <div className="relative" data-testid="principles-list">
            <div className="absolute left-8 top-4 bottom-4 w-px bg-tan hidden md:block" aria-hidden="true" />
            <div className="space-y-10">
              {principles.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start" data-testid={`principle-${i}`}>
                    <div className="md:col-span-2 flex md:justify-center">
                      <div className="relative bg-cream w-16 h-16 flex items-center justify-center border border-tan">
                        <span className="font-display text-2xl text-cobalt">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-4">
                      <h3 className="font-display text-2xl lg:text-3xl text-ink leading-tight">{p.title}</h3>
                    </div>
                    <div className="md:col-span-6">
                      <p className="text-ink-soft leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-20 bg-ink text-cream">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <p className="font-display text-3xl lg:text-4xl leading-tight max-w-xl">
            Curious what this could look like for your work?
          </p>
          <Link
            to="/collaborate"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-orange-brand text-cream px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-orange-soft transition-colors"
          >
            Let's talk <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
